<template>
  <section>
    <div class="md:border-b md:mb-15">
      <TheHeader
        with-menu
        back-route="/investments"
      />
    </div>
    <div
      v-if="investment"
      class="md:hidden min-h-60"
    >
      <BaseSection class="mb-2">
        <BaseTitleGroup
          :title="participation.attributes.name"
          :subtitle="$t('My investments')"
        />
      </BaseSection>
      <BaseSection class="mb-4 investment-details">
        <div class="base-border-b pb-m">
          <BaseGreyText class="mb-1">
            {{ operation.attributes.date | moment('DD/MM/YYYY') }}
          </BaseGreyText>
          <BaseOrangeText>
            <BaseCurrencySpan :value="investment.data.attributes.totalAmount" />
            investis
          </BaseOrangeText>
        </div>
      </BaseSection>
      <BaseSection>
        <BaseSectionHeader>
          {{ $t('Investment details') }}
        </BaseSectionHeader>
        <BaseMenuList :items="menuItems"/>
      </BaseSection>
    </div>
    <ColLayout
      v-if="investment"
      :title="participation.attributes.name"
      :preview-text="participation.attributes.previewText"
      :subtitle="$t('My investments')"
    >
      <tab
        id="Investment details"
        :name="$t('Investments')"
      >
        <InvestmentDetailsContent />
      </tab>
      <tab
        id="Investment reporting"
        :name="$t('Reportings')"
      >
        <BaseSectionHeader class="mb-4">
          {{ $t('Quarterly reports') }}
        </BaseSectionHeader>
        <div class="mb-6">
          <BaseBulletList
            v-if="reportings.length"
            :items="transformDocuments(reportings)"
          />
          <BaseEmptyState
            v-else
            :src="image"
            alt="Reportings empty state"
            class="pt-4"
          >
            <span v-html="$t('Reportings empty state')" />
          </BaseEmptyState>
        </div>
      </tab>
      <tab
        id="Admin SPV"
        :name="`Admin ${spv.attributes.name}`"
      >
        <BaseSectionHeader class="mb-2">
          {{ $t('Company Admin', { spv: spv.attributes.name }) }}
        </BaseSectionHeader>
        <div class="mb-6">
          <BaseBulletList :items="transformDocuments(companyAdminDocuments)">
            <li
              key="captable"
              class="cursor-pointer"
            >
              <a
                :href="captableUrl"
                target="_blank"
              >
                Captable
              </a>
            </li>
          </BaseBulletList>
        </div>
        <div class="mb-6">
          <BaseSectionHeader class="mb-2">
            {{ $t('Financial Statements', { spv: spv.attributes.name }) }}
          </BaseSectionHeader>
          <BaseBulletList
            v-if="financialStatementDocuments.length"
            :items="transformDocuments(financialStatementDocuments)"
          />
          <BaseEmptyState
            v-else
            :src="image"
            alt="Financial statement empty state"
          >
            {{ $t('Financial statement empty state', { spv: spv.attributes.name }) }}
          </BaseEmptyState>
        </div>
        <div
          class="mb-6"
          v-if="operationsDocuments.length"
        >
          <BaseSectionHeader class="mb-2">
            {{ $t('Operations documents') }}
          </BaseSectionHeader>
          <BaseBulletList :items="transformDocuments(operationsDocuments)" />
        </div>
        <div
          class="mb-6"
          v-if="peaDocuments.length"
        >
          <BaseSectionHeader class="mb-2">
            {{ $t('PEA Details') }}
          </BaseSectionHeader>
          <BaseBulletList :items="transformDocuments(peaDocuments)" />
        </div>
      </tab>
      <template slot="right">
        <div class="flex justify-between pb-6 border-b border-strong-shadow-color mb-4">
          <div class="flex flex-col">
            <span class="text-xl mb-2px">{{ participation.attributes.name }}</span>
            <BaseGreyText class="mb-1">
              {{ operation.attributes.date | moment('DD/MM/YYYY') }}
            </BaseGreyText>
            <BaseOrangeText>
              <BaseCurrencySpan :value="investment.data.attributes.totalAmount" />
              {{ $t('invested') }}
            </BaseOrangeText>
          </div>
          <BaseRoundLogo :src="participation.attributes.logo" />
        </div>
        <div>
          <CompanyDetails
            :attributes="participation.attributes"
            :spv-attributes="spv.attributes"
            :date="operation.attributes.date"
          />
        </div>
      </template>
    </ColLayout>
    <TheFooter />
  </section>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import loadInvestment from '@/mixins/loadInvestment';
import TheHeader from '@/components/TheHeader.vue';
import BaseSection from '@/components/BaseSection.vue';
import BaseTitleGroup from '@/components/BaseTitleGroup.vue';
import BaseGreyText from '@/components/BaseGreyText.vue';
import BaseOrangeText from '@/components/BaseOrangeText.vue';
import BaseSectionHeader from '@/components/BaseSectionHeader.vue';
import BaseMenuList from '@/components/BaseMenuList.vue';
import BaseCurrencySpan from '@/components/BaseCurrencySpan.vue';
import BaseRoundLogo from '@/components/BaseRoundLogo.vue';
import BaseBulletList from '@/components/BaseBulletList.vue';
import BaseEmptyState from '@/components/BaseEmptyState.vue';
import TheFooter from '@/components/TheFooter.vue';
import CompanyDetails from './partials/CompanyDetails.vue';
import ColLayout from './partials/ColLayout.vue';
import InvestmentDetailsContent from './partials/InvestmentDetailsContent.vue';
import image from '../assets/document.svg';
import { API_URL } from '../constants';

export default {
  name: 'InvestmentShow',
  components: {
    TheHeader,
    BaseSection,
    BaseTitleGroup,
    BaseGreyText,
    BaseOrangeText,
    BaseSectionHeader,
    BaseMenuList,
    BaseCurrencySpan,
    BaseRoundLogo,
    BaseBulletList,
    CompanyDetails,
    ColLayout,
    InvestmentDetailsContent,
    BaseEmptyState,
    TheFooter,
  },
  mixins: [loadInvestment],
  data() {
    return {
      baseUrl: `/investments/${this.$route.params.id}/`,
      image,
    };
  },
  computed: {
    ...mapGetters('investment', [
      'reportings',
      'financialStatementDocuments',
      'companyAdminDocuments',
      'operationsDocuments',
      'peaDocuments',
    ]),
    menuItems() {
      return [
        {
          title: 'Investissements',
          routeName: `${this.baseUrl}details`,
        },
        {
          title: 'Reportings',
          routeName: `${this.baseUrl}reporting`,
        },
        {
          title: `Admin ${get(this.spv, 'attributes.name')}`,
          routeName: `${this.baseUrl}documents`,
        },
      ];
    },
  },
};
</script>

<style lang="css" scoped>
.flex-2 {
  flex: 2;
}
</style>
